import { _, am5, am5xy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { getColor } from '../../../../helpers';

export const createAxisAndSeries = ({
  root,
  chart,
  data,
  numberFormatLeft,
  numberFormatRight,
  xAxisRenderer,
  inSameScale,
  isLargeDataset,
}: {
  root: am5.Root;
  chart: am5xy.XYChart;
  data: types.widgets.TableData;
  numberFormatLeft: string;
  numberFormatRight: string;
  xAxisRenderer: am5xy.ValueAxis<am5xy.AxisRenderer>;
  inSameScale: string[] | undefined;
  isLargeDataset: boolean;
}) => {
  function createAxisAndSeries(keys: string[], index: number, opposite: boolean) {
    const createdSeries: am5xy.LineSeries[] = [];
    const yRenderer = am5xy.AxisRendererY.new(root, {});
    if (!(index == 0 && keys.length > 1)) {
      yRenderer.set('opposite', opposite);
    }
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        numberFormat: opposite ? numberFormatRight : numberFormatLeft,
        maxDeviation: 1,
        renderer: yRenderer,
      })
    );

    yAxis.get('renderer').labels.template.setAll({
      fontSize: 11,
      textAlign: 'center',
    });

    if (chart.yAxes.indexOf(yAxis) > 0) {
      yAxis.set('syncWithAxis', chart.yAxes.getIndex(0) as any);
    }

    keys.forEach((key, localIdx) => {
      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: key,
          xAxis: xAxisRenderer,
          yAxis: yAxis,
          valueYField: key,
          valueXField: 'date',
          fill: am5.color(getColor(index + localIdx)),
          stroke: am5.color(getColor(index + localIdx)),
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText: '{name}: {valueY}',
          }),
          minDistance: isLargeDataset ? 0.2 : 0,
        })
      );

      series.get('tooltip')?.label.setAll({
        fontSize: 11,
      });

      series.strokes.template.setAll({ strokeWidth: 1 });

      if (!(index == 0 && keys.length > 1)) {
        yAxis.get('renderer').labels.template.setAll({
          fill: series.get('stroke'),
        });
        yAxis.get('renderer').grid.template.setAll({
          strokeWidth: 0.2,
        });
        yAxis.get('renderer').setAll({
          strokeOpacity: 1,
          strokeWidth: 1,
          stroke: series.get('stroke'),
        });
      }

      series.data.processor = am5.DataProcessor.new(root, {
        dateFormat: 'MMM dd yyyy',
        dateFields: ['date'],
      });

      series.data.setAll(data);
      createdSeries.push(series);
    });
    return createdSeries;
  }

  const dataKeys = Object.keys(_.omit(data[0], 'date'));
  const onLeft = _.isEmpty(inSameScale) ? [dataKeys[0]] : inSameScale.filter((key) => dataKeys.includes(key));
  const onRight = dataKeys.filter((key) => !onLeft.includes(key));

  const leftSeries = createAxisAndSeries(onLeft, 0, false);
  const rightSeries = createAxisAndSeries(onRight, 0 + onLeft.length, true);

  return [...leftSeries, ...rightSeries];
};
